import { useState } from 'react';
import { Phone, MapPin, Clock, CalendarPlus } from '@phosphor-icons/react';
import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { Text } from '@astryxdesign/core/Text';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { List, ListItem } from '@astryxdesign/core/List';
import { Lightbox } from '@astryxdesign/core/Lightbox';
import { Section } from '@astryxdesign/core/Section';
import { Link } from '@astryxdesign/core/Link';
import { Photo } from '@/components/Photo';
import { mediaUrl } from '@/lib/supabase';
import { formatPhone } from '@/lib/phone';
import { money } from '@/lib/money';
import { durationLabel } from '@/lib/time';
import { photosOf, useBookingRoute, useLoadedTenant, useSlug } from './hooks';
import { todayHoursLabel, weekSummary } from './schedule';

export function HomePage() {
  const t = useLoadedTenant();
  const slug = useSlug();
  const { go } = useBookingRoute();
  const hero = photosOf(t, 'hero')[0];
  const works = photosOf(t, 'work');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const p = t.profile;
  const featured = t.services.slice(0, 4);

  return (
    <>
      {/* Главное фото: название и запись поверх снимка, кнопка двигается вместе с фото */}
      <header className="relative h-[72svh] min-h-96 overflow-hidden">
        <Photo path={hero?.path} alt={`${t.name}: главное фото`} eager className="absolute inset-0 size-full" />
        <div className="hero-shade absolute inset-0" aria-hidden />
        <div className="safe-top absolute inset-x-0 bottom-0 px-4 pb-6">
          <VStack gap={3}>
            <VStack gap={1}>
              <h1 className="text-4xl leading-none font-semibold tracking-tight text-primary text-balance">{t.name}</h1>
              <Text color="secondary">{p.tagline}</Text>
            </VStack>
            <Button
              label="Записаться"
              variant="primary"
              size="lg"
              width="100%"
              icon={<CalendarPlus size={20} weight="bold" />}
              onClick={() => go('service')}
            />
          </VStack>
        </div>
      </header>

      <Section variant="transparent" padding={4}>
        <VStack gap={3}>
          <HStack gap={2} align="center">
            <Clock size={18} className="text-secondary" aria-hidden />
            <Text>{todayHoursLabel(t)}</Text>
          </HStack>
          <HStack gap={2} align="start">
            <MapPin size={18} className="mt-0.5 shrink-0 text-secondary" aria-hidden />
            {p.map_url ? (
              <Link href={p.map_url} isExternalLink color="primary">
                {p.address}
              </Link>
            ) : (
              <Text>{p.address}</Text>
            )}
          </HStack>
          {p.phone ? (
            <Button label={`Позвонить ${formatPhone(p.phone)}`} href={`tel:${p.phone}`} icon={<Phone size={18} />} variant="secondary" width="100%" />
          ) : null}
        </VStack>
      </Section>

      {p.info_cards.length ? (
        <Section variant="transparent" padding={4} paddingBlockStart={0}>
          <ul className="grid gap-2">
            {p.info_cards.map((c) => (
              <li key={c.title} className="rounded-lg border border-border bg-surface p-4">
                <Text weight="semibold" display="block">
                  {c.title}
                </Text>
                <Text color="secondary" display="block">
                  {c.text}
                </Text>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section variant="transparent" padding={0} paddingBlockStart={2}>
        <List
          hasDividers
          header={
            <HStack justify="between" align="center" paddingInline={4}>
              <Heading level={2}>Услуги и цены</Heading>
              <Link href={`/s/${slug}/services`} isStandalone>
                Все услуги
              </Link>
            </HStack>
          }
        >
          {featured.map((s) => (
            <ListItem
              key={s.id}
              label={s.name}
              description={durationLabel(s.duration_minutes)}
              endContent={
                <Text weight="semibold" hasTabularNumbers>
                  {money(s.price_cents, t.currency, s.price_is_from)}
                </Text>
              }
              onClick={() => go('time', { svc: s.id })}
            />
          ))}
        </List>
      </Section>

      {works.length ? (
        <Section variant="transparent" padding={0} paddingBlockStart={6}>
          <VStack gap={3}>
            <HStack paddingInline={4}>
              <Heading level={2}>Наши работы</Heading>
            </HStack>
            <ul className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2" aria-label="Фото работ">
              {works.map((w, i) => (
                <li key={w.id} className="w-64 shrink-0 snap-start">
                  <button
                    type="button"
                    onClick={() => setLightbox(i)}
                    className="block w-full overflow-hidden rounded-lg text-left focus-visible:outline-2 focus-visible:outline-accent-bg"
                    aria-label={`Открыть фото: ${w.caption || 'работа студии'}`}
                  >
                    <Photo path={w.path} alt={w.caption || 'Работа студии'} className="aspect-4/5 w-full" />
                  </button>
                  {w.caption ? (
                    <Text color="secondary" display="block" maxLines={2}>
                      {w.caption}
                    </Text>
                  ) : null}
                </li>
              ))}
            </ul>
          </VStack>
          <Lightbox
            isOpen={lightbox !== null}
            onOpenChange={(o) => !o && setLightbox(null)}
            index={lightbox ?? 0}
            onIndexChange={setLightbox}
            media={works.map((w) => ({ type: 'image' as const, src: mediaUrl(w.path) ?? '', alt: w.caption || 'Работа студии', caption: w.caption || undefined }))}
          />
        </Section>
      ) : null}

      <Section variant="transparent" padding={4} paddingBlockStart={6}>
        <VStack gap={2}>
          <Heading level={2}>Часы приёма</Heading>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1">
            {weekSummary(t).map((r) => (
              <div key={r.days} className="contents">
                <dt className="text-secondary">{r.days}</dt>
                <dd className="tabular text-primary">{r.hours}</dd>
              </div>
            ))}
          </dl>
          {p.description ? <Text color="secondary">{p.description}</Text> : null}
        </VStack>
      </Section>
    </>
  );
}
